import {
  basePathRewrite,
  cetgoriesCount,
  commonPropsForSSR,
  ConductUseExperiment,
  getEJendPoints,
  getunlockBannerExperiment,
  getURLSearchParams,
} from '@components/common';
import companiesMapping from '@components/common/companiesMapping.json';
import companyRedirect from '@components/common/companyWithRedirect.json';
import {
  getFilterQueryString,
  mapQueryParams,
} from '@components/common/filterHelper';
import { Company } from '@license-admin/boldjobs';
import { fetchCompanyQnA, modifyJobDetails } from 'API/jobs';
import { apiCallList } from 'API/wlb/ApiClient';
import { BaseURL } from 'helper';
import { getExperimentVariant } from 'helper/experiments/experimentActions';
import { RMCPortalExperiments } from 'helper/experiments/experimentConstants';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useState } from 'react';
const ReactPaginate = dynamic(() => import('react-paginate'), { ssr: false });

function removeUndefined(obj: Record<string, any>) {
  return JSON.parse(JSON.stringify(obj));
}

export default function CompanyDetailsPage({
  themeType,
  localization,
  jobCardData,
  company_details,
  isPremiumUser,
  deviceType,
  top_companies,
  companyqna,
  headcount,
  hiring_count,
  remote_percentage,
  isubname,
  isubname2,
  ititle,
  ititle2,
  seo,
  companyDescription,
  companyLogo,
  companyTitle,
  isLongDesc,
  unlockBannerExperiment,
  userAgent,
}: any) {
  const router = useRouter();
  const [isUnlockBannerExpVariant, setUnlockBannerExpVariant] =
    useState<number>(0);
  const { companyname } = router.query;
  if (!router.isReady || typeof companyname !== 'string') {
    return <p>Loading...</p>;
  }

  const strParts: string[] = [];
  if (isubname) strParts.push(`${isubname},`);
  if (ititle) strParts.push(ititle);
  if (isubname2) strParts.push(`& ${isubname2},`);
  if (ititle2) strParts.push(ititle2);
  const str = `${strParts.join(' ')} - Interview with Remote.co`;
  // PayWall Experiment
  const expDataItems = {
    deviceType,
    experimentsMap: RMCPortalExperiments.rmcPaywallDesktopMobile,
    userAgent,
    callback: (experimentData: any) => {
      // Add any actions needed for this experiment
      if (experimentData?.variant) {
        // Store variant or perform other actions if needed
        setUnlockBannerExpVariant(experimentData.variant);
      }
    },
  };
  ConductUseExperiment(expDataItems);

  return (
    <>
      <Head>
        <title>{`Remote Work at ${
          company_details?.name || companyname
        } - Remote.co`}</title>
        <meta
          name="description"
          content={
            seo?.description ||
            `
            Find remote jobs at ${
              company_details?.name || companyname
            }. Explore work-from-home roles in tech, support, marketing, and more—now hiring remote talent.
          ` ||
            company_details?.summary
          }
        />{' '}
        <meta
          name="robots"
          content={
            company_details && company_details.summary
              ? 'follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large'
              : 'noindex, nofollow'
          }
        />{' '}
        <meta property="og:locale" content="en_US" />{' '}
        <meta property="og:type" content="website" />{' '}
        <meta
          property="og:title"
          content={`Remote Work at ${companyname} - Remote.co`}
        />{' '}
        <meta
          property="og:description"
          content={seo?.description || company_details?.summary}
        />{' '}
        <meta
          property="og:url"
          content={`${BaseURL}/company/${company_details?.slug}`}
        />{' '}
        <meta property="og:site_name" content="Remote.co" />{' '}
        <meta
          property="article:publisher"
          content="https://www.facebook.com/remote.co"
        />{' '}
        <meta property="og:updated_time" content="2025-01-14T03:27:32-04:00" />{' '}
        <meta name="twitter:card" content="summary_large_image" />{' '}
        <meta
          name="twitter:title"
          content={`Remote Work at ${companyname} - Remote.co`}
        />{' '}
        <meta
          name="twitter:description"
          content={seo?.description || company_details?.summary}
        />{' '}
        <meta name="twitter:site" content="@remote_co" />{' '}
        <meta name="twitter:creator" content="@remote_co" />
        <link rel="canonical" href={`${BaseURL}/company/${companyname}`} />
        <script
          type="application/ld+json"
          className="yoast-schema-graph"
          key="yoast-schema"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@graph': [
                {
                  '@type': 'WebPage',
                  '@id': `${BaseURL}/company/${company_details?.slug}#webpage`,
                  url: `${BaseURL}/company/${company_details?.slug}`,
                  name: `Remote Work at ${company_details?.name} - Remote.co`,
                  isPartOf: {
                    '@type': 'WebSite',
                    '@id': `${BaseURL}#website`,
                    url: `${BaseURL}`,
                    name: `Remote Work at ${company_details?.name} - Remote.co`,
                    publisher: {
                      '@type': 'Organization',
                      '@id': `${BaseURL}#organization`,
                      name: 'Remote.co',
                      url: `${BaseURL}`,
                      sameAs: [
                        'https://www.facebook.com/remote.co',
                        'https://twitter.com/remote_co',
                      ],
                      logo: {
                        '@type': 'ImageObject',
                        '@id': `${BaseURL}#/schema/logo/image/`,
                        inLanguage: 'en-US',
                        url: 'https://remoteco.s3.amazonaws.com/wp-content/uploads/2020/08/31174509/remoteco.png',
                        contentUrl:
                          'https://remoteco.s3.amazonaws.com/wp-content/uploads/2020/08/31174509/remoteco.png',
                        width: '400',
                        height: '400',
                        caption: 'Remote.co',
                      },
                      image: {
                        '@type': 'ImageObject',
                        '@id': `${BaseURL}/company/{${company_details?.slug}}#/schema/logo/image/`,
                        inLanguage: 'en-US',
                        url: 'https://remoteco.s3.amazonaws.com/wp-content/uploads/2020/08/31174509/remoteco.png',
                        contentUrl:
                          'https://remoteco.s3.amazonaws.com/wp-content/uploads/2020/08/31174509/remoteco.png',
                        width: '400',
                        height: '400',
                        caption: 'Remote.co',
                      },
                    },
                    inLanguage: 'en-US',
                  },
                  datePublished: 'June 8, 2021',
                  dateModified: 'November 18, 2021',
                  inLanguage: 'en-US',
                  potentialAction: {
                    '@type': 'ReadAction',
                    target: {
                      '@type': 'EntryPoint',
                      urlTemplate: `${BaseURL}/company/${company_details?.slug}`,
                    },
                  },
                },
                {
                  '@type': 'BreadcrumbList',
                  '@id': `${BaseURL}/company/${company_details?.slug}#breadcrumb`,
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: '1',
                      item: {
                        '@id': `${BaseURL}`,
                        name: 'Home',
                      },
                    },
                    {
                      '@type': 'ListItem',
                      position: '2',
                      item: {
                        '@id': `${BaseURL}/qa-leading-remote-companies`,
                        name: 'Companies',
                      },
                    },
                    {
                      '@type': 'ListItem',
                      position: '3',
                      item: {
                        '@id': `${BaseURL}/company/${company_details?.slug}`,
                        name: company_details?.name,
                      },
                    },
                  ],
                },
              ],
            }),
          }}
        />
        <link
          rel="preload"
          as="image"
          href="/blobcontent/rmc/images/job-list-image.webp"
          type="image/webp"
        />
      </Head>
      <Company
        isPremiumUser={isPremiumUser}
        deviceType={deviceType}
        companyData={company_details}
        companyName={companyname}
        basePathRewrite={basePathRewrite}
        isBreadCrumb={true}
        JobCards={jobCardData?.jobs?.results}
        pageCount={jobCardData?.jobs?.totalPages}
        hiring_count={hiring_count}
        faqData={companyqna}
        featuredCompanies={top_companies}
        themeType={themeType}
        localization={localization}
        cetgoriesCount={cetgoriesCount}
        interview_with={str}
        ReactPaginate={ReactPaginate}
        companyDesc={companyDescription}
        companyLogo={companyLogo}
        companyTitle={companyTitle}
        isLongDesc={isLongDesc}
        unlockBannerExperiment={getunlockBannerExperiment(
          isUnlockBannerExpVariant
        )}
      />
    </>
  );
}

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  let { req, query } = context;
  query = mapQueryParams(query);
  const queryString: string = getFilterQueryString(query);
  const pageNumber: string = getURLSearchParams(queryString, 'page');
  const ejEndPoints = getEJendPoints();
  const commonSSRProps = await commonPropsForSSR(req, 'company_page');

  const companyname: string = context.params?.companyname as string;
  let companyData = null;

  if (companyname) {
    const companyRedirectData = companyRedirect.find(
      (company) => company.company === companyname
    );
    if (companyRedirectData) {
      return {
        redirect: {
          destination: `${BaseURL}/${companyRedirectData.redirect}`,
          permanent: true,
        },
      };
    }
  }

  if (companyname) {
    const companyMapping = companiesMapping.find(
      (company) => company.rmc === companyname
    );
    if (companyMapping) {
      companyData = companyMapping;
    }
  }

  const { cookieHeader, isPremiumUser } = commonSSRProps;

  const {
    companyqna = [],
    headcount = null,
    remote_percentage = null,
    isubname = '',
    isubname2 = '',
    ititle = '',
    ititle2 = '',
    seo = {
      title: '',
      description: '',
    },
  } = await fetchCompanyQnA(req.url, cookieHeader);

  // Ensure all props are serializable
  const cleanedProps = removeUndefined(commonSSRProps);
  let jobCardData = [];
  const apiEndpoints: any = {
    hiring_count: ejEndPoints.hiring_count,
    company_details: {
      ...ejEndPoints.company_details,
      url: ejEndPoints.company_details.url.replace(
        '{0}',
        companyData?.fj || (context.params?.companyname as string)
      ),
    },
    top_companies: {
      ...ejEndPoints.top_companies,
      url: ejEndPoints.top_companies.url.replace('{0}', '5'),
    },
  };

  if (isPremiumUser) {
    apiEndpoints.job_details = ejEndPoints.job_details;
    modifyJobDetails(
      apiEndpoints.job_details,
      `search?CompaniesSlug=${
        companyData?.fj || context.params?.companyname
      }&page=${pageNumber || 1}`,
      { SortByPostedDate: true, jobStatus: true }
    );
  }

  const data = await apiCallList({ apiEndpoints, cookieHeader, req });

  const {
    job_details,
    company_details,
    top_companies,
    hiring_count = { jobsCount: 0, companiesCount: 0 },
  } = data;
  jobCardData = job_details || {};

  if (!company_details) {
    return {
      redirect: {
        destination: `${BaseURL}/qa-leading-remote-companies`,
        permanent: true,
      },
    };
  }

  Object.assign(company_details, companyData || { summary: '' });

  if (!isPremiumUser) {
    jobCardData = {
      ...jobCardData,
      jobs: {
        ...jobCardData.jobs,
        results: [],
        totalPages: 0,
      },
    };
  }
  const companyDetails = company_details.summary || '';
  const isLongDesc = companyDetails.length > 382;
  const companyDescription = isLongDesc
    ? `${companyDetails.slice(0, 382)}...`
    : companyDetails;
  const companyLogo = company_details?.logo;
  const companyTitle = company_details.name;

  if (Array.isArray(top_companies) && top_companies.length > 0) {
    top_companies.forEach((company) => {
      const mapping = companiesMapping.find((c) => c.rmc === company.slug);
      if (!mapping) {
        company.isrmcwpJob = true;
      }
    });
  }
  const expPaywallData = getExperimentVariant(
    RMCPortalExperiments.rmcPaywallDesktopMobile.id,
    cookieHeader
  );

  return {
    props: {
      ...cleanedProps,
      companyqna,
      hiring_count,
      headcount,
      remote_percentage,
      jobCardData,
      company_details,
      top_companies,
      isubname,
      isubname2,
      ititle,
      ititle2,
      seo,
      fullwidth: true,
      companyDescription,
      companyLogo,
      companyTitle,
      isLongDesc,
      unlockBannerExperiment: expPaywallData || 0,
      userAgent: req?.headers['user-agent'] ?? '',
    },
  };
};
